import { useNavigate } from 'react-router-dom';
import { BarChart3, Users, Building2, Calendar, ArrowRight, CheckCircle2, DollarSign, Star, Zap, Shield } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

export default function Landing() {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const goToSignup = () => {
    navigate('/login', { state: { isSignup: true } });
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-dark' : 'bg-white'}`}>
      {/* Navigation */}
      <nav className={`border-b ${theme === 'dark' ? 'border-dark-200' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <span className={`ml-2 text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Le Suivi</span>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/login')}
                className={`px-4 py-2 ${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}
              >
                Connexion
              </button>
              <button
                onClick={goToSignup}
                className="px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90 transition-colors"
              >
                Commencer
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative overflow-hidden pt-16 sm:pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-accent-purple to-accent-blue">
              Gérez vos campagnes d'influence
            </h1>
            <p className={`mt-6 text-xl ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} max-w-3xl mx-auto`}>
              Simplifiez la gestion de vos collaborations et suivez vos performances en temps réel avec notre plateforme tout-en-un.
            </p>
            <div className="mt-8 flex justify-center gap-4">
              <button
                onClick={goToSignup}
                className="px-8 py-4 bg-gradient-to-r from-accent-purple to-accent-blue text-white rounded-lg hover:from-accent-purple/90 hover:to-accent-blue/90 transition-colors font-medium"
              >
                Essayer gratuitement
              </button>
              <button
                onClick={goToSignup}
                className={`px-8 py-4 ${
                  theme === 'dark'
                    ? 'bg-dark-100 text-white hover:bg-dark-200 border-dark-200'
                    : 'bg-white text-gray-900 hover:bg-gray-50 border-gray-200'
                } rounded-lg transition-colors font-medium border`}
              >
                Voir une démo
              </button>
            </div>
          </div>

          {/* Feature Grid */}
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Zap,
                title: "Rapide et intuitif",
                description: "Interface optimisée pour une gestion efficace de vos campagnes d'influence."
              },
              {
                icon: Shield,
                title: "Sécurisé",
                description: "Vos données sont protégées avec les plus hauts standards de sécurité."
              },
              {
                icon: Star,
                title: "Support premium",
                description: "Une équipe dédiée pour vous accompagner dans votre réussite."
              }
            ].map((feature, index) => (
              <div
                key={index}
                className={`${
                  theme === 'dark'
                    ? 'bg-dark-50 border-dark-200 hover:border-accent-blue/50'
                    : 'bg-white border-gray-200 hover:border-accent-blue/50'
                } border rounded-xl p-6 transition-colors group`}
              >
                <div className="w-12 h-12 rounded-lg bg-accent-blue/10 flex items-center justify-center mb-4 group-hover:bg-accent-blue/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-accent-blue" />
                </div>
                <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
                  {feature.title}
                </h3>
                <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className={`py-24 ${theme === 'dark' ? 'bg-dark-50' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Fonctionnalités principales
            </h2>
            <p className={`mt-4 text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Tout ce dont vous avez besoin pour gérer vos campagnes d'influence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: BarChart3,
                title: "Tableau de bord",
                description: "Visualisez vos KPIs et l'état de vos campagnes en temps réel"
              },
              {
                icon: Users,
                title: "Gestion des influenceurs",
                description: "Centralisez les informations de vos créateurs de contenu"
              },
              {
                icon: Building2,
                title: "Suivi client",
                description: "Gérez vos relations clients et leurs campagnes"
              },
              {
                icon: Calendar,
                title: "Planning",
                description: "Organisez vos campagnes avec un calendrier intégré"
              },
              {
                icon: DollarSign,
                title: "Facturation",
                description: "Automatisez la gestion des paiements"
              },
              {
                icon: CheckCircle2,
                title: "Validation",
                description: "Simplifiez le processus de validation des contenus"
              }
            ].map((feature, index) => (
              <div
                key={index}
                className={`${
                  theme === 'dark'
                    ? 'bg-dark-100 border-dark-200 hover:border-accent-blue/50'
                    : 'bg-white border-gray-200 hover:border-accent-blue/50'
                } rounded-xl p-6 border transition-all group`}
              >
                <div className="w-12 h-12 rounded-lg bg-accent-blue/10 flex items-center justify-center mb-4 group-hover:bg-accent-blue/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-accent-blue" />
                </div>
                <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
                  {feature.title}
                </h3>
                <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-accent-purple to-accent-blue rounded-2xl p-1">
            <div className={theme === 'dark' ? 'bg-dark' : 'bg-white' + ' rounded-xl'}>
              <div className="max-w-7xl mx-auto py-12 px-4 sm:py-16 sm:px-6 lg:px-8 lg:py-20">
                <div className="max-w-4xl mx-auto text-center">
                  <h2 className={`text-3xl font-extrabold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} sm:text-4xl`}>
                    Adopté par les meilleures agences
                  </h2>
                  <p className={`mt-3 text-xl ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Rejoignez les agences qui font confiance à Le Suivi
                  </p>
                </div>
                <dl className="mt-10 text-center sm:max-w-3xl sm:mx-auto sm:grid sm:grid-cols-3 sm:gap-8">
                  <div className="flex flex-col">
                    <dt className={`order-2 mt-2 text-lg leading-6 font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Campagnes gérées
                    </dt>
                    <dd className="order-1 text-5xl font-extrabold text-accent-blue">1000+</dd>
                  </div>
                  <div className="flex flex-col mt-10 sm:mt-0">
                    <dt className={`order-2 mt-2 text-lg leading-6 font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Influenceurs
                    </dt>
                    <dd className="order-1 text-5xl font-extrabold text-accent-blue">500+</dd>
                  </div>
                  <div className="flex flex-col mt-10 sm:mt-0">
                    <dt className={`order-2 mt-2 text-lg leading-6 font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Agences
                    </dt>
                    <dd className="order-1 text-5xl font-extrabold text-accent-blue">50+</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className={`py-24 ${theme === 'dark' ? 'bg-dark-50' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`${
            theme === 'dark'
              ? 'bg-dark-100 border-dark-200'
              : 'bg-white border-gray-200'
          } rounded-2xl border p-8 md:p-12 lg:p-16`}>
            <div className="text-center max-w-3xl mx-auto">
              <h2 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
                Prêt à optimiser vos campagnes ?
              </h2>
              <p className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-8`}>
                Commencez dès maintenant et découvrez comment Le Suivi peut transformer votre gestion d'influence.
              </p>
              <button
                onClick={goToSignup}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-accent-purple to-accent-blue text-white rounded-lg hover:from-accent-purple/90 hover:to-accent-blue/90 transition-colors font-medium"
              >
                Commencer gratuitement
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className={`${theme === 'dark' ? 'bg-dark border-dark-200' : 'bg-white border-gray-200'} border-t`}>
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <span className={`ml-2 text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Le Suivi</span>
            </div>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600' + ' text-sm'}>
              © 2024 Le Suivi. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}